import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { FilePdf, ImageSquare, LinkSimple, InstagramLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { saveBlob } from "@/utils/pdfExport";
import GaleriaShareButton from "@/components/GaleriaShareButton";
import { CertificateCard, CertificateSquare, ScaledCertificate, certificateUrl } from "@/components/exam/Certificate";

export function downloadImage(blob, name) {
  const url = URL.createObjectURL(blob);
  let inIframe = true;
  try {
    inIframe = window.top !== window.self;
  } catch {
    inIframe = true;
  }
  if (inIframe) {
    window.open(url, "_blank", "noopener");
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export default function CertificatePanel({ cert }) {
  const { lang } = useTranslation();
  const es = lang === "es";
  const exportRef = useRef(null);
  const squareRef = useRef(null);
  const [busy, setBusy] = useState("");
  const base = `dslr-advisor-certificate-${cert.cert_id}`;

  const render = () => html2canvas(exportRef.current, { scale: 2, backgroundColor: "#0A0A0A", useCORS: true });

  const asPdf = async () => {
    setBusy("pdf");
    try {
      const canvas = await render();
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [1000, 700], hotfixes: ["px_scaling"] });
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, 1000, 700);
      saveBlob(pdf.output("blob"), `${base}.pdf`);
    } finally {
      setBusy("");
    }
  };

  const asImage = async () => {
    setBusy("img");
    try {
      const canvas = await render();
      canvas.toBlob((b) => b && downloadImage(b, `${base}.png`), "image/png");
    } finally {
      setBusy("");
    }
  };

  const asPost = async () => {
    setBusy("post");
    try {
      const canvas = await html2canvas(squareRef.current, { scale: 1, width: 1080, height: 1080, backgroundColor: "#0A0A0A", useCORS: true });
      canvas.toBlob((b) => b && downloadImage(b, `${base}-post-1080.png`), "image/png");
    } finally {
      setBusy("");
    }
  };

  const copyLink = async () => {
    const url = certificateUrl(cert.cert_id);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(es ? "Enlace copiado" : "Link copied");
    } catch {
      toast.message(url);
    }
  };

  return (
    <div className="space-y-4" data-testid="certificate-panel">
      <ScaledCertificate cert={cert} lang={lang} />
      <div aria-hidden style={{ position: "fixed", left: -12000, top: 0, pointerEvents: "none" }}>
        <CertificateCard ref={exportRef} cert={cert} lang={lang} />
        <CertificateSquare ref={squareRef} cert={cert} lang={lang} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={asPost} disabled={!!busy} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium" data-testid="certificate-download-post">
          <InstagramLogo weight="fill" className="w-4 h-4 mr-1.5" />
          {busy === "post" ? (es ? "Generando..." : "Generating...") : es ? "Imagen para publicar (1080×1080)" : "Ready-to-post image (1080×1080)"}
        </Button>
        <Button onClick={asPdf} disabled={!!busy} variant="outline" className="border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524]/10" data-testid="certificate-download-pdf">
          <FilePdf weight="fill" className="w-4 h-4 mr-1.5" />
          {busy === "pdf" ? (es ? "Generando..." : "Generating...") : es ? "Descargar PDF" : "Download PDF"}
        </Button>
        <Button onClick={asImage} disabled={!!busy} variant="outline" className="border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524]/10" data-testid="certificate-download-image">
          <ImageSquare weight="fill" className="w-4 h-4 mr-1.5" />
          {busy === "img" ? (es ? "Generando..." : "Generating...") : es ? "Descargar imagen" : "Download image"}
        </Button>
        <Button onClick={copyLink} variant="outline" className="border-white/15 text-[#D4D4D8] hover:bg-white/5" data-testid="certificate-copy-link">
          <LinkSimple weight="bold" className="w-4 h-4 mr-1.5" />
          {es ? "Copiar enlace público" : "Copy public link"}
        </Button>
        <GaleriaShareButton testId="certificate-share-galeria" />
      </div>
      <p className="text-[#71717A] text-xs" data-testid="certificate-public-note">
        {es
          ? "Cualquier persona con el enlace público puede ver tu nombre, puntaje y fecha."
          : "Anyone with the public link can see your name, score and date."}
      </p>
    </div>
  );
}
